import { EnhancedScoringService } from '../../../shared/services/enhancedScoringService';
import { EnhancedQuestion, UserAnswer } from '../../../types/questionFormats';

const parseStoredSelectionList = (storedAnswer: string): Array<string | number> => {
  try {
    const parsed = JSON.parse(storedAnswer);
    if (Array.isArray(parsed)) {
      return parsed
        .map(value => {
          if (typeof value === 'number') return value;
          if (typeof value !== 'string') return null;

          const trimmed = value.trim();
          if (trimmed.length === 0) return null;

          const numericValue = Number(trimmed);
          return Number.isFinite(numericValue) ? numericValue : trimmed;
        })
        .filter((value): value is string | number => value !== null);
    }
  } catch {
    // Fall back to legacy comma-separated parsing below.
  }

  return storedAnswer
    .split(',')
    .map(value => {
      const trimmed = value.trim();
      if (trimmed.length === 0) return null;

      const numericValue = Number(trimmed);
      return Number.isFinite(numericValue) ? numericValue : trimmed;
    })
    .filter((value): value is string | number => value !== null);
};

export const parseStoredMultiplayerAnswer = (
  question: EnhancedQuestion,
  storedAnswer: string | undefined,
): string | UserAnswer | null => {
  if (!storedAnswer) return null;

  switch (question.question_type) {
    case 'mcq_single':
      return {
        format: 'mcq_single',
        answer: storedAnswer,
      };
    case 'true_false':
      return {
        format: 'true_false',
        answer: storedAnswer.trim().toLowerCase() === 'true',
      };
    case 'mcq_multiple': {
      return {
        format: 'mcq_multiple',
        answers: parseStoredSelectionList(storedAnswer) as unknown as Extract<UserAnswer, { format: 'mcq_multiple' }>['answers'],
      };
    }
    case 'fill_blank': {
      try {
        const parsed = JSON.parse(storedAnswer);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          return {
            format: 'fill_blank',
            answers: Object.fromEntries(
              Object.entries(parsed).map(([blankId, value]) => [blankId, String(value ?? '')]),
            ),
          };
        }
      } catch {
        return null;
      }

      return null;
    }
    case 'ordering': {
      try {
        const parsed = JSON.parse(storedAnswer);
        if (Array.isArray(parsed)) {
          return {
            format: 'ordering',
            order: parsed
              .map(value => Number(value))
              .filter(value => Number.isFinite(value)),
          };
        }
      } catch {
        // Fall back to legacy comma-separated values below.
      }

      return {
        format: 'ordering',
        order: storedAnswer
          .split(',')
          .map(value => Number(value.trim()))
          .filter(value => Number.isFinite(value)),
      };
    }
    default:
      return storedAnswer;
  }
};

export const getRawCorrectCount = (
  questions: EnhancedQuestion[],
  answers: Record<string, string>,
): number => questions.reduce((count, question) => {
  if (question.id == null) {
    return count;
  }

  const storedAnswer = answers[String(question.id)];
  const parsedAnswer = parseStoredMultiplayerAnswer(question, storedAnswer);

  if (!parsedAnswer) {
    return count;
  }

  if (typeof parsedAnswer === 'string') {
    const isCorrect = parsedAnswer === question.correct_answer || parsedAnswer === question.answer;
    return isCorrect ? count + 1 : count;
  }

  const result = EnhancedScoringService.calculateQuestionScore(question, parsedAnswer, 30);
  return result.percentage === 100 ? count + 1 : count;
}, 0);
